<template>
  <DxDataGrid
    :id="props.id"
    ref="grid"
    :keyExpr="props.keyExpr"
    :data-source="dataSource"
    :hoverStateEnabled="true"
    :allowColumnReordering="props.allowStateSave"
    :allowColumnResizing="props.allowColumnResizing"
    columnResizingMode="widget"
    :onRowUpdating="prepareUpdate"
    :onEditorPreparing="onEditorPreparing"
    :onCellPrepared="onCellPrepared"
    :onRowInserted="onRowInserted"
    :onRowRemoved="onRowRemoved"
    :onRowUpdated="onRowUpdated"
    :onSelectionChanged="onSelectionChanged"
    :onRowPrepared="onRowPrepared"
    :onContentReady="onContentReady"
    @toolbar-preparing="onToolbarPreparing"
    @editing-start="onRowEditStart"
    :onInitNewRow="onInitNewRow"
    v-model:selected-row-keys="selectedRows"
    :no-data-text="$t('common.messages.noData')"
    :cacheEnabled="false">
    <DxSorting :mode="sortMode" />
    <DxStateStoring
      :enabled="props.allowStateSave"
      type="custom"
      :custom-load="loadState"
      :custom-save="saveState"
      :saving-timeout="500" />
    <DxColumnChooser
      :enabled="props.allowStateSave"
      mode="select"
      :title="$t('common.labels.columnChooser')" />
    <DxRemoteOperations :paging="true" :sorting="true" :filtering="true" />
    <DxPaging :page-size="10" />
    <DxPager
      :show-page-size-selector="true"
      :allowed-page-sizes="[10, 20, 50]"
      :show-navigation-buttons="true" />
    <slot />
    <template v-for="name in Object.keys($slots).filter(n => n !== 'default')" 
             :key="name" #[name]="data">
      <slot :name="name" v-bind="data" />
    </template>
  </DxDataGrid>
</template>

<script setup>
import { DxDataGrid } from 'devextreme-vue';
import { DxColumnChooser, DxPager, DxPaging, DxRemoteOperations, DxSorting, DxStateStoring } from 'devextreme-vue/cjs/data-grid';
import { CustomStore } from 'devextreme/common/data';
import { Workbook } from 'exceljs';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { formatDate as dxFormat } from 'devextreme/localization';

import { useApiClient } from '@/utils/apiClient';
const api = useApiClient();

import { useLocalization } from '@/locales/localization';
const localization = useLocalization();

import { formGrids } from '@/utils/formGrids';
const grid = ref(null);

onMounted(() => {
  formGrids.add(props.id, grid.value);
});

onUnmounted(() => {
  formGrids.remove(props.id);
});

const selectedRows = ref([]);

var props = defineProps({
  id: { type: String }, // Should be globally unique, as the state is saved using this value as key
  keyExpr: { type: String },
  allowStateSave: { type: Boolean, default: true },
  loadAction: { type: Object },
  insertAction: { type: Object },
  updateAction: { type: Object },
  removeAction: { type: Object },
  editorPreparingAction: { type: Object },
  cellPreparedAction: { type: Object },
  rowInsertedAction: { type: Object },
  sortEnabled: { type: Boolean, default: true },
  toolbarOptions: { type: Array },
  usePaging: { type: Boolean, default: true},
  allowColumnResizing: { type: Boolean, default: true },
  rowRemovedAction: { type: Object },
  rowUpdatedAction: { type: Object },
  allowExport: { type: Boolean, default: false },
  exportData: { type: Object, default: null },
  exportColumns: { type: Array, default: null },
  selectionChangedAction: { type: Object },
  rowPreparedAction: { type: Object },
  contentReadyAction: { type: Object }
});

var emit = defineEmits(["onRowEditStart", "onInitNewRow"]);

defineExpose({ grid, selectedRows });

function onToolbarPreparing(o){
  if(props.allowExport === true){
    o.toolbarOptions.items.push(
      {
        location: 'after',
        widget: 'dxButton',
        options: {
          icon: 'exportxlsx',
          hint: localization.t('common.actions.export'),
          onClick: () => exportXlsx()
        }
      }
    );
  }
  
  const filterRowVisible = grid.value.instance.option('filterRow.visible');
  if(filterRowVisible === true){
    o.toolbarOptions.items.push({
      location: 'after',
      widget: 'dxButton',
      options: {
        icon: 'arrowback',
        hint: localization.t('common.actions.clearFilters'),
        onClick: () => clearFilters()
      }
    });
  }

  if(!props.toolbarOptions) return;
  props.toolbarOptions.forEach(m => {
    o.toolbarOptions.items.push(m);
  });
}

async function exportXlsx(){
  if(props.exportData === null) return;
  var data = await props.exportData();

  var workbook = new Workbook();
  var worksheet = workbook.addWorksheet();

  var exportColumns = props.exportColumns ?? grid.value.instance.getColumns().filter(c => c.dataField && c.allowExporting !== false);
  const headers = exportColumns.map(c => c.caption);

  worksheet.addRow(headers);
  data.forEach(o => {
    var row = exportColumns.map(m => {
      // Split column datafield attribute and traverse object to find the correct datafield
      var value = m.dataField.split('.').reduce((n, p) => n?.[p], o);
      
      // Apply date format for rows of type date or datetime
      if((m.dataType === "date" || m.dataType === "datetime") && m.format && value !== undefined && value !== null){
        return dxFormat(new Date(value), m.format);
      }

      return value;
    });
    worksheet.addRow(row);
  });
  worksheet.getRow(1).font = { bold: true };
  
  const buffer = await workbook.xlsx.writeBuffer();

  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats.officedocument.spreatsheet.sheet"
  });

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "export.xlsx";
  a.click();
  URL.revokeObjectURL(url);
}

function clearFilters(){
  const columns = grid.value.instance.getColumns();
  columns.forEach(o => grid.value.instance.columnOption(o.dataField, "filterValue", null));
}

async function loadState() {
  var result = await api.get('/account/usergridstateget', { gridName: props.id });
  
  var state = result.data;
  // Reset loaded state to the first page
  if(state) state.pageIndex = 0;
  
  return state;
}

function saveState(state) {
  var model = { gridName: props.id, state: JSON.stringify(state) };
  api.post('/account/usergridstateset', model);
}

const dataSource = new CustomStore({
    key: 'id',
    load: props.loadAction,
    insert: props.insertAction,
    update: props.updateAction,
    remove: props.removeAction,
});

function prepareUpdate(o) {
  // DxDataGrid's default behavior for updating rows is to only include modified columns,
  // but the server expects the entire object
  o.newData = {...o.oldData, ...o.newData};
}

const onEditorPreparing = (e) => {
  if (props.editorPreparingAction !== null && typeof props.editorPreparingAction != 'undefined') {
    props.editorPreparingAction(e);
  } 
};

const onCellPrepared = (e) => {
  if (props.cellPreparedAction !== null && typeof props.cellPreparedAction != 'undefined') {
    props.cellPreparedAction(e);
  } 
};

const onRowInserted = (e) => {
  if (props.rowInsertedAction !== null && typeof props.rowInsertedAction != 'undefined') {
    props.rowInsertedAction(e);
  } 
};

const onRowUpdated = (e) => {
  if (props.rowUpdatedAction !== null && typeof props.rowUpdatedAction != 'undefined') {
    props.rowUpdatedAction(e);
  } 
};

const onRowRemoved = (e) => {
  if (props.rowRemovedAction !== null && typeof props.rowRemovedAction != 'undefined'){
    props.rowRemovedAction(e);
  }
};

const onSelectionChanged = (e) => {
  if (props.selectionChangedAction !== null && typeof props.selectionChangedAction != 'undefined'){
    props.selectionChangedAction(e);
  }
};

const onRowPrepared = (e) => {
  if (props.rowPreparedAction !== null && typeof props.rowPreparedAction != 'undefined'){
    props.rowPreparedAction(e);
  }
};

const onContentReady = (e) => {
  if (props.contentReadyAction !== null && typeof props.contentReadyAction != 'undefined'){
    props.contentReadyAction(e);
  }
};

const sortMode = computed(() => {
  if (props.sortEnabled === true) return 'single';
  return 'none';
});

function onRowEditStart(o){
  emit("onRowEditStart", o);
}

function onInitNewRow(o){
  emit("onInitNewRow", o);
}
</script>
